import React, { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { getTodayString } from '../../services/reminderService';
import { computeAnchoredPopoverPosition } from './anchoredPopover';

export interface DateTimePickerProps {
  /** Combined value as `YYYY-MM-DDTHH:mm`, or empty string when unset. */
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  /** Optional id prefix for stable test selectors. */
  testId?: string;
  'aria-label'?: string;
}

const WEEKDAYS = ['Lu', 'Ma', 'Mi', 'Ju', 'Vi', 'Sá', 'Do'] as const;
const MONTHS = [
  'Enero',
  'Febrero',
  'Marzo',
  'Abril',
  'Mayo',
  'Junio',
  'Julio',
  'Agosto',
  'Septiembre',
  'Octubre',
  'Noviembre',
  'Diciembre',
] as const;

const HOURS = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0'));
const MINUTES = ['00', '05', '10', '15', '20', '25', '30', '35', '40', '45', '50', '55'];

const POPOVER_WIDTH = 288;
const POPOVER_Z_INDEX = 10050;

function parseValue(value: string): { due: string; time: string } {
  if (!value || !value.includes('T')) {
    return { due: '', time: '09:00' };
  }
  const [due, timePart] = value.split('T');
  const time = (timePart || '09:00').slice(0, 5);
  return { due: due || '', time: /^\d{2}:\d{2}$/.test(time) ? time : '09:00' };
}

function formatDisplay(value: string): string {
  const { due, time } = parseValue(value);
  if (!due) return '';
  const [y, m, d] = due.split('-').map(Number);
  if (!y || !m || !d) return `${due} ${time}`;
  const date = new Date(y, m - 1, d);
  const weekday = date.toLocaleDateString('es-ES', { weekday: 'short' });
  return `${weekday} ${d}/${m}/${y} · ${time}`;
}

function buildCalendarDays(year: number, month: number): Array<{ date: Date; inMonth: boolean }> {
  const first = new Date(year, month, 1);
  // Monday-first: JS getDay() Sunday=0 → shift
  const startOffset = (first.getDay() + 6) % 7;
  const gridStart = new Date(year, month, 1 - startOffset);
  return Array.from({ length: 42 }, (_, i) => {
    const date = new Date(gridStart);
    date.setDate(gridStart.getDate() + i);
    return { date, inMonth: date.getMonth() === month };
  });
}

function toDueString(date: Date): string {
  return getTodayString(date);
}

function composeValue(due: string, time: string): string {
  if (!due) return '';
  return `${due}T${time || '09:00'}`;
}

/**
 * Token-styled date + time popover (replaces native `datetime-local`).
 * Calendar panel is portaled to `document.body` so overflow parents cannot clip it.
 */
export const DateTimePicker: React.FC<DateTimePickerProps> = ({
  value,
  onChange,
  placeholder = 'Fecha y hora',
  disabled = false,
  testId = 'datetime-picker',
  'aria-label': ariaLabel = 'Seleccionar fecha y hora',
}) => {
  const parsed = parseValue(value);
  const initialCursor = parsed.due
    ? new Date(Number(parsed.due.slice(0, 4)), Number(parsed.due.slice(5, 7)) - 1, 1)
    : new Date();

  const [open, setOpen] = useState(false);
  const [viewYear, setViewYear] = useState(initialCursor.getFullYear());
  const [viewMonth, setViewMonth] = useState(initialCursor.getMonth());
  const [draftDue, setDraftDue] = useState(parsed.due);
  const [draftTime, setDraftTime] = useState(parsed.time);
  const [coords, setCoords] = useState<{ top: number; left: number }>({ top: 0, left: 0 });

  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const listboxId = useId();

  useEffect(() => {
    if (!open) return;
    const next = parseValue(value);
    setDraftDue(next.due);
    setDraftTime(next.time);
    if (next.due) {
      setViewYear(Number(next.due.slice(0, 4)));
      setViewMonth(Number(next.due.slice(5, 7)) - 1);
    }
  }, [open, value]);

  const updatePosition = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    const anchor = trigger.getBoundingClientRect();
    const popover = popoverRef.current;
    const width = popover?.offsetWidth || POPOVER_WIDTH;
    const height = popover?.offsetHeight || 360;
    setCoords(computeAnchoredPopoverPosition(anchor, width, height));
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [open, updatePosition, viewYear, viewMonth]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (triggerRef.current?.contains(target) || popoverRef.current?.contains(target)) {
        return;
      }
      setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const days = useMemo(() => buildCalendarDays(viewYear, viewMonth), [viewYear, viewMonth]);
  const todayStr = getTodayString();
  const display = formatDisplay(value);

  const shiftMonth = (delta: number) => {
    const next = new Date(viewYear, viewMonth + delta, 1);
    setViewYear(next.getFullYear());
    setViewMonth(next.getMonth());
  };

  const apply = (due: string, time: string) => {
    onChange(composeValue(due, time));
  };

  const selectDay = (date: Date) => {
    const due = toDueString(date);
    setDraftDue(due);
    apply(due, draftTime);
  };

  const selectTime = (part: 'hour' | 'minute', next: string) => {
    const [h, m] = (draftTime || '09:00').split(':');
    const time = part === 'hour' ? `${next}:${m || '00'}` : `${h || '09'}:${next}`;
    setDraftTime(time);
    if (draftDue) {
      apply(draftDue, time);
    } else {
      // Prefer today when the user picks a time first
      const due = todayStr;
      setDraftDue(due);
      apply(due, time);
    }
  };

  const clear = () => {
    setDraftDue('');
    setDraftTime('09:00');
    onChange('');
    setOpen(false);
    triggerRef.current?.focus();
  };

  const snapMinute = (raw: string): string => {
    const n = Number(raw);
    if (Number.isNaN(n)) return '00';
    const nearest = Math.round(n / 5) * 5;
    const clamped = Math.min(55, Math.max(0, nearest));
    return String(clamped).padStart(2, '0');
  };

  const hourValue = (draftTime || '09:00').slice(0, 2);
  const minuteValue = snapMinute((draftTime || '09:00').slice(3, 5));

  const popover =
    open &&
    createPortal(
      <div
        ref={popoverRef}
        id={listboxId}
        role="dialog"
        aria-label={ariaLabel}
        data-testid={`${testId}-popover`}
        style={{
          position: 'fixed',
          top: coords.top,
          left: coords.left,
          zIndex: POPOVER_Z_INDEX,
          width: POPOVER_WIDTH,
          maxWidth: 'min(288px, calc(100vw - 16px))',
          borderRadius: 12,
          background: 'var(--z-surface)',
          border: 'none',
          boxShadow: 'var(--z-sh-floating)',
          overflow: 'hidden',
          padding: 12,
          boxSizing: 'border-box',
        }}
      >
        {/* Month header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
          <button
            type="button"
            aria-label="Mes anterior"
            data-testid={`${testId}-prev-month`}
            onClick={() => shiftMonth(-1)}
            style={navBtnStyle}
          >
            ‹
          </button>
          <span
            style={{
              flex: '1 1 auto',
              textAlign: 'center',
              fontSize: 13,
              fontWeight: 700,
              color: 'var(--z-text)',
              letterSpacing: '-0.01em',
            }}
          >
            {MONTHS[viewMonth]} {viewYear}
          </span>
          <button
            type="button"
            aria-label="Mes siguiente"
            data-testid={`${testId}-next-month`}
            onClick={() => shiftMonth(1)}
            style={navBtnStyle}
          >
            ›
          </button>
        </div>

        {/* Weekday labels */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(7, 1fr)',
            gap: 2,
            marginBottom: 4,
          }}
        >
          {WEEKDAYS.map((d) => (
            <span
              key={d}
              style={{
                textAlign: 'center',
                fontSize: 10,
                fontWeight: 700,
                color: 'var(--z-muted)',
                padding: '2px 0',
              }}
            >
              {d}
            </span>
          ))}
        </div>

        {/* Day grid */}
        <div
          role="grid"
          aria-label="Calendario"
          style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2 }}
        >
          {days.map(({ date, inMonth }) => {
            const due = toDueString(date);
            const selected = draftDue === due;
            const isToday = due === todayStr;
            return (
              <button
                key={due + String(inMonth)}
                type="button"
                role="gridcell"
                aria-label={due}
                aria-selected={selected}
                data-testid={`${testId}-day-${due}`}
                onClick={() => selectDay(date)}
                style={{
                  height: 30,
                  borderRadius: 8,
                  border: 'none',
                  background: selected
                    ? 'var(--z-accent-soft)'
                    : isToday
                      ? 'var(--z-warn-bg)'
                      : 'transparent',
                  color: !inMonth
                    ? 'var(--z-faint)'
                    : selected
                      ? 'var(--z-accent-copy)'
                      : 'var(--z-text-2)',
                  fontSize: 12,
                  fontWeight: selected || isToday ? 700 : 500,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                {date.getDate()}
              </button>
            );
          })}
        </div>

        {/* Time row */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            marginTop: 12,
            paddingTop: 10,
            borderTop: 'none',
          }}
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
            aria-hidden
            style={{ color: 'var(--z-muted-2)', flex: '0 0 auto' }}
          >
            <circle cx="12" cy="12" r="9" />
            <path d="M12 7v5l3 2" />
          </svg>
          <label style={{ display: 'flex', alignItems: 'center', gap: 4, flex: '1 1 auto' }}>
            <span className="sr-only">Hora</span>
            <select
              aria-label="Hora"
              data-testid={`${testId}-hour`}
              value={hourValue}
              onChange={(e) => selectTime('hour', e.target.value)}
              style={selectStyle}
            >
              {HOURS.map((h) => (
                <option key={h} value={h}>
                  {h}
                </option>
              ))}
            </select>
            <span style={{ color: 'var(--z-muted)', fontWeight: 700 }}>:</span>
            <select
              aria-label="Minutos"
              data-testid={`${testId}-minute`}
              value={minuteValue}
              onChange={(e) => selectTime('minute', e.target.value)}
              style={selectStyle}
            >
              {MINUTES.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
          </label>
        </div>

        {/* Footer actions */}
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginTop: 10 }}>
          <button
            type="button"
            aria-label="Quitar fecha y hora"
            data-testid={`${testId}-clear`}
            onClick={clear}
            style={{
              ...footerBtnStyle,
              color: 'var(--z-muted-2)',
              background: 'transparent',
              border: 'none',
            }}
          >
            Quitar
          </button>
          <button
            type="button"
            aria-label="Listo"
            data-testid={`${testId}-done`}
            onClick={() => {
              setOpen(false);
              triggerRef.current?.focus();
            }}
            style={{
              ...footerBtnStyle,
              color: 'var(--z-on-accent)',
              background: 'var(--z-accent)',
              border: 'none',
              fontWeight: 700,
            }}
          >
            Listo
          </button>
        </div>
      </div>,
      document.body
    );

  return (
    <div style={{ position: 'relative', flex: '1 1 auto', minWidth: 0 }} data-testid={testId}>
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        aria-label={ariaLabel}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? listboxId : undefined}
        data-testid={`${testId}-trigger`}
        onClick={() => setOpen((prev) => !prev)}
        style={{
          width: '100%',
          minHeight: 28,
          height: 28,
          padding: '0 10px',
          borderRadius: 8,
          border: 'none',
          outline: 'none',
          boxShadow: 'none',
          background: open ? 'var(--z-accent-soft)' : 'var(--z-fill)',
          color: display ? 'var(--z-text-2)' : 'var(--z-muted)',
          fontFamily: 'inherit',
          fontSize: 12,
          fontWeight: display ? 600 : 500,
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          cursor: disabled ? 'not-allowed' : 'pointer',
          boxSizing: 'border-box',
          textAlign: 'left',
        }}
      >
        <svg
          width="13"
          height="13"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden
          style={{ flex: '0 0 auto', color: 'var(--z-warn)' }}
        >
          <rect x="3" y="4" width="18" height="18" rx="2" />
          <path d="M16 2v4M8 2v4M3 10h18" />
        </svg>
        <span style={{ flex: '1 1 auto', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {display || placeholder}
        </span>
      </button>
      {popover}
    </div>
  );
};

const navBtnStyle: React.CSSProperties = {
  width: 28,
  height: 28,
  borderRadius: 8,
  border: 'none',
  background: 'var(--z-fill-2)',
  color: 'var(--z-text-2)',
  cursor: 'pointer',
  fontSize: 16,
  fontWeight: 700,
  lineHeight: 1,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontFamily: 'inherit',
};

const selectStyle: React.CSSProperties = {
  flex: '1 1 auto',
  height: 30,
  borderRadius: 8,
  border: 'none',
  background: 'var(--z-fill)',
  color: 'var(--z-text-2)',
  fontFamily: "'JetBrains Mono', monospace",
  fontSize: 12,
  fontWeight: 600,
  padding: '0 6px',
  cursor: 'pointer',
};

const footerBtnStyle: React.CSSProperties = {
  height: 30,
  padding: '0 12px',
  borderRadius: 8,
  fontSize: 12,
  fontFamily: 'inherit',
  cursor: 'pointer',
};
